// Shared nav model for the redesigned top nav + mobile bottom tab bar.
//
// One source of truth for the primary destinations so the desktop header and
// the mobile tab bar stay in sync. The two surfaces order the items
// differently (the design puts Access Reviews mid-list on desktop but last in
// the tab bar), so we keep a record + per-surface ordered id lists.
//
// Mirrors the design `TopNav` / `MobileTabBar` items in
// `employee-portal 2/project/components.jsx`.

import { useMemo } from "react";
import type { IconType } from "react-icons";
import MFMembers from "state/members/membersState";
import { filterNavItems, visibleNavIds } from "./navVisibility";
import {
  TbApps,
  TbChecklist,
  TbClipboardCheck,
  TbHome,
  TbShieldCheck,
} from "react-icons/tb";

export interface NavItem {
  id: string;
  /** Full label used in the desktop top nav. */
  label: string;
  /** Shorter label used under the mobile tab-bar icon. */
  shortLabel: string;
  to: string;
  /** Tab-bar glyph (Tabler, matching the design's `ti-*` icons). */
  icon: IconType;
  /** True when `pathname` should highlight this item. */
  isActive: (pathname: string) => boolean;
  /** Tasks carries the awaiting-action count badge. */
  hasCount?: boolean;
}

const home: NavItem = {
  id: "home",
  label: "Home",
  shortLabel: "Home",
  to: "/members",
  icon: TbHome,
  isActive: (p) => p === "/" || p === "/members" || p === "/members/",
};

const access: NavItem = {
  id: "access",
  label: "My Access",
  shortLabel: "Access",
  to: "/members/my-access",
  icon: TbShieldCheck,
  isActive: (p) => p.startsWith("/members/my-access"),
};

const reviews: NavItem = {
  id: "reviews",
  label: "Access Reviews",
  shortLabel: "Reviews",
  to: "/members/access-reviews",
  icon: TbClipboardCheck,
  isActive: (p) => p.startsWith("/members/access-reviews"),
};

const tasks: NavItem = {
  id: "tasks",
  label: "Tasks",
  shortLabel: "Tasks",
  to: "/members/assigned-tasks",
  icon: TbChecklist,
  hasCount: true,
  isActive: (p) => p.startsWith("/members/assigned-tasks"),
};

const catalog: NavItem = {
  id: "catalog",
  label: "Catalog",
  shortLabel: "Catalog",
  to: "/members/catalog",
  icon: TbApps,
  isActive: (p) =>
    p.startsWith("/members/catalog") ||
    p.startsWith("/members/access/catalogs"),
};

/** Desktop top-nav order. */
export const DESKTOP_NAV: NavItem[] = [home, access, reviews, tasks, catalog];

/** Mobile bottom tab-bar order (Reviews moves to the end). */
export const MOBILE_TABS: NavItem[] = [home, access, tasks, catalog, reviews];

/**
 * The above narrowed to the destinations the org enabled in Portal settings —
 * both surfaces filter the same way, so desktop and mobile stay in step.
 */
export const useVisibleNavItems = (items: NavItem[]): NavItem[] => {
  const config = MFMembers.useGetPortalConfig();
  return filterNavItems(items, config?.navItems);
};

/**
 * The same selection as a set of ids, for surfaces that mirror the nav without
 * rendering it.
 *
 * Home is a dashboard of doorways: each card summarises one of these
 * destinations and links into it, so a card whose destination the org removed
 * has to go too (PLO-5580). Reading the rendered nav rather than the stored
 * selection keeps the two in step through `filterNavItems`'s fail-open cases.
 */
export const useVisibleNavIds = (): ReadonlySet<string> => {
  const config = MFMembers.useGetPortalConfig();
  const enabled = config?.navItems;
  return useMemo(() => visibleNavIds(DESKTOP_NAV, enabled), [enabled]);
};
